import React from 'react';
import { Trash2, Video, CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { REX_FIELDS } from '../../../utils/rexSynthesis';

const API = process.env.REACT_APP_BACKEND_URL;

export const SOURCE_LABELS = {
  expired: { label: 'Fin de licence', tone: 'bg-warn/15 text-warn border-warn/40' },
  expiring: { label: 'Derniers jours', tone: 'bg-accent/15 text-accent border-accent/40' },
  admin: { label: 'Demandé par admin', tone: 'bg-purple-500/15 text-purple-300 border-purple-400/40' },
};

/** Admin — carte d'un REX (textes complets + vidéo + sélection pour la synthèse). */
export default function RexCard({ rex, busy, selected, onToggleSelect, onToggleRead, onRemove, openVideo, onOpenVideo }) {
  const src = SOURCE_LABELS[rex.source] || SOURCE_LABELS.expiring;
  return (
    <div
      className={`bg-ink-800 border rounded-xl p-5 ${selected ? 'border-money/60 ring-1 ring-money/30' : rex.read_at ? 'border-ink-600' : 'border-accent/50'}`}
      data-testid={`rex-card-${rex.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(rex.id)}
            className="w-4 h-4 accent-emerald-500 cursor-pointer"
            title="Inclure dans la synthèse anonymisée"
            data-testid={`rex-select-${rex.id}`}
          />
          {!rex.read_at && <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />}
          <div>
            <p className="font-bold text-foreground text-sm">{rex.holder_name || '—'}</p>
            <p className="text-[10px] text-slate-500 font-mono">{rex.license_key} · {new Date(rex.created_at).toLocaleString('fr-FR')}</p>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold ${src.tone}`}>{src.label}</span>
          {rex.video_token && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-money/10 text-money border border-money/40 font-bold flex items-center gap-1">
              <Video className="w-3 h-3" /> Vidéo
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onToggleRead(rex)}
            disabled={busy}
            className={`p-1.5 rounded-lg ${rex.read_at ? 'text-slate-500 hover:bg-ink-700/40' : 'text-money hover:bg-money/10'}`}
            title={rex.read_at ? 'Marquer non lu' : 'Marquer comme lu'}
            data-testid={`rex-toggle-read-${rex.id}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : rex.read_at ? <Circle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          </button>
          <button onClick={() => onRemove(rex)} className="p-1.5 rounded-lg text-danger hover:bg-danger/10" title="Supprimer" data-testid={`rex-delete-${rex.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        {REX_FIELDS.map((f) => (
          <div key={f.id} className="bg-background border border-ink-600 rounded-lg p-3">
            <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">{f.label}</p>
            <p className="text-xs text-slate-300 whitespace-pre-wrap">{(rex[f.id] || '').trim() || <em className="text-slate-600">— non renseigné —</em>}</p>
          </div>
        ))}
      </div>

      {rex.video_token && (
        <div className="mt-3">
          {openVideo ? (
            <video controls autoPlay playsInline src={`${API}/api/feedback/rex/video/${rex.video_token}`} className="w-full max-h-80 rounded-lg bg-black" data-testid={`rex-video-player-${rex.id}`} />
          ) : (
            <button onClick={() => onOpenVideo(rex.id)} className="flex items-center gap-2 px-4 py-2 border border-money/40 text-money rounded-lg text-xs font-bold hover:bg-money/10" data-testid={`rex-play-video-${rex.id}`}>
              <Video className="w-4 h-4" /> Lire le témoignage vidéo
              {rex.video_size ? <span className="text-slate-500 font-normal">({Math.round(rex.video_size / 1024 / 1024 * 10) / 10} Mo)</span> : null}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
